import LoadingSpinner from "@/components/loading-spinner/LoadingSpinner";
import { ChevronLeftOutlined as ChevronLeft } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronLeftOutlined";
import classnames from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import faqClient from "../../clients/faqClient";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import FAQFeedbackForm from "../../components/faq-feedback-form/FAQFeedbackForm";
import FAQMoreHelp from "../../components/faq-more-help/FAQMoreHelp";
import FAQPopularSolutions from "../../components/faq-popular-solutions/FAQPopularSolutions";
import FAQRelatedSolutions from "../../components/faq-related-solutions/FAQRelatedSolutions";
import FAQSearchForm from "../../components/faq-search-form/FAQSearchForm";
import Solution from "../../components/solution/Solution";
import analyticsTrigger, { dispatchNewEvent } from "../../utils/analytics/trigger/analyticsTrigger";
import { getSlugForSolution } from "../../utils/solutionSlug";
import { useExperience, trackMonetateEvent } from "../../context/MonetateContext";
import styles from "./faq.module.scss";

export const metadata = {
  title: `FAQ | Customer Services`,
  description: `Have a question? We're here to help with all your queries, from delivery and returns to orders and your online account and your My John Lewis membership.`,
};

function FAQ({ inContactUs = false, showTitle = true }) {
  const titleRef = useRef();
  const router = useRouter();
  const {
    pathname,
    query: { q, ...rest },
    push,
    asPath,
  } = router;
  const [query, setQuery] = useState(q);
  const [resultsLoading, setResultsLoading] = useState(true);
  const [searchError, setSearchError] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loadingPopular, setLoadingPopular] = useState(!inContactUs);
  const [solutionId, setSolutionId] = useState("");
  const [related, setRelated] = useState(null);
  const [relatedLoading, setRelatedLoading] = useState(true);
  const showRelated = !relatedLoading && related && related.length > 0;
  // profile param no longer used

  const newFAQSearchBarExperiment = useExperience(
    "AB-5672 |  AB/ABn Test |  Customer & Account  | Increase prominence/placement of FAQ search",
  );

  const isFAQSearchBarExperiment = newFAQSearchBarExperiment?.parameters?.variant === "experiment";

  const faqRequestHeaders = useMemo(() => {
    if (isFAQSearchBarExperiment && !asPath.toLowerCase().includes("faq")) {
      return { "Experiment-Group": true };
    }
    return {};
  }, [isFAQSearchBarExperiment, asPath]);

  const { data: solution, isValidating } = useSWR(
    inContactUs && solutionId ? `/solutions/${solutionId}` : null,
    async url => {
      try {
        const faqClientInstance = await faqClient(false, faqRequestHeaders);
        const response = await faqClientInstance.get(url);
        return response.data;
      } catch (err) {
        return { error: err.response && err.response.data };
      }
    },
  );

  const fetchResults = async (queryStr, didYouMean) => {
    if (!queryStr) return {};

    try {
      const faqClientInstance = await faqClient(false, faqRequestHeaders);
      const response = await faqClientInstance.get(`/search`, {
        params: {
          q: queryStr,
        },
      });

      if (
        !response.data.didYouMean ||
        response.data.totalHits > 0 ||
        (didYouMean && didYouMean.initialStr === didYouMean.suggestedStr)
      ) {
        return {
          results: { ...response.data, didYouMean },
        };
      }

      return fetchResults(response.data.didYouMean, {
        initialStr: queryStr,
        suggestedStr: response.data.didYouMean,
      });
    } catch (error) {
      return { error: error.response && error.response.data };
    }
  };

  const fetchRelated = async () => {
    try {
      const faqClientInstance = await faqClient(false, faqRequestHeaders);
      const response = await faqClientInstance.get(`/solutions/${solutionId}/related`);
      return response.data.relatedSolutions.slice(0, 5).map(({ id, description: title }) => ({ id, title }));
    } catch {
      return [];
    }
  };

  const fetchNewRelated = () => {
    setRelatedLoading(true);
    fetchRelated().then(result => {
      setRelated(result);
      setRelatedLoading(false);
    });
  };

  const fetchNewSearchResults = async () => {
    setSearchResults(null);
    setSearchError(null);
    setResultsLoading(true);
    setRelated(null);
    setRelatedLoading(false);

    const resultsData = await fetchResults(query, undefined);

    setSearchResults(resultsData.results);
    setSearchError(resultsData.error);
    setResultsLoading(false);
  };

  useEffect(() => {
    if (solutionId) {
      dispatchNewEvent("dataStateChange", {
        action: "faq.Event",
        type: "answerViewed",
        answerId: solutionId,
      });
      fetchNewRelated();
    }
  }, [solutionId]);

  useEffect(() => {
    setQuery(q);

    fetchNewSearchResults();
    if (q) {
      dispatchNewEvent("dataStateChange", {
        action: "faq.Event",
        type: "search",
        searchTerm: q,
      });
    }
  }, [q]);

  const fetchPopular = async () => {
    try {
      const faqClientInstance = await faqClient(false, faqRequestHeaders);
      const response = await faqClientInstance.get(`/topsolutions`);
      return response.data.solutions.slice(0, 5).map(({ solutionID: id, description: title }) => ({ id, title }));
    } catch (error) {
      return [];
    }
  };

  useEffect(() => {
    fetchNewSearchResults();

    if (!inContactUs) {
      analyticsTrigger("dataStateChange", pathname, undefined, "pageload");
      fetchPopular().then(solutions => {
        setPopular(solutions);
        setLoadingPopular(false);
      });
    }
  }, []);

  const reloadWithQuery = qry => {
    setResultsLoading(true);
    setQuery(qry);

    push(
      {
        pathname,
        query: { q: qry, ...rest },
      },
      undefined,
      { shallow: true, scroll: false },
    );
  };

  const handleSubmit = event => {
    setSuggestions([]);
    event.preventDefault();
    if (query !== q && query !== "") {
      reloadWithQuery(query);
    }
  };

  const handleSearchChange = async str => {
    const faqClientInstance = await faqClient(false, faqRequestHeaders);

    if (str) {
      const suggestions = await faqClientInstance.get(`/suggestions`, {
        params: {
          q: str,
        },
      });

      setSuggestions(suggestions.data);
      trackMonetateEvent("faq_search_performed");
      if (suggestions?.data?.length === 0) {
        trackMonetateEvent("zero_search_result_faq");
      }
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionSelected = async suggestion => {
    setSuggestions([]);
    setQuery(suggestion);
    setResultsLoading(true);
    reloadWithQuery(suggestion);
  };

  const handleFeedback = async ({ solved, comment }) => {
    dispatchNewEvent("dataStateChange", {
      action: "faq.Event",
      type: "feedbackSubmitted",
      feedback: solved,
      comment: comment ? "1" : "0",
    });

    const faqClientInstance = await faqClient(false, faqRequestHeaders);
    await faqClientInstance
      .post(`/solutions/${solutionId}/feedback`, {
        solved: solved === "yes",
        comments: comment,
      })
      .catch();
  };

  const noSearchResults = inContactUs ? (
    <p className={styles.no__results}>
      Sorry, we couldn&#39;t find any results for your query. Try searching with different keywords or question.
      <br />
      <span className={styles["no-result-text"]}>Alternatively you can contact the Customer Services team on</span>
      <span className={styles["contact-number"]}>03456 100 344</span>7 days a week, 8:00am - 9:00pm
    </p>
  ) : (
    <p className={styles.faq__error}>
      Sorry, we couldn’t find any results for your query. Try searching with different keywords or go back to look at
      the topics on our <Link href="/">Customer Services page</Link>.
    </p>
  );
  const displayPopular = !loadingPopular && popular.length > 0 && (!searchResults || searchResults.totalHits === 0);

  return (
    <>
      {!inContactUs ? <Breadcrumb links={[{ text: "Customer services", href: "/" }]} /> : null}
      <div ref={titleRef} className="util-container">
        <div className={styles.faq__form}>
          <FAQSearchForm
            onSubmit={handleSubmit}
            onSearchChange={handleSearchChange}
            onSuggestionSelected={handleSuggestionSelected}
            query={query}
            suggestions={suggestions}
            setQuery={setQuery}
            inContactUs={inContactUs}
            setSolutionId={setSolutionId}
            showTitle={showTitle}
          />

          {resultsLoading && (
            <div className={styles.faq__spinner}>
              <LoadingSpinner />
            </div>
          )}

          {!resultsLoading &&
            searchResults &&
            (searchResults.totalHits > 0 ? (
              <>
                {searchResults.didYouMean && !solutionId ? (
                  <p className={styles.faq__suggestion}>
                    Sorry we have no search results for <strong>‘{searchResults.didYouMean.initialStr}’</strong> but we
                    found results for <strong>‘{searchResults.didYouMean.suggestedStr}’</strong>.
                  </p>
                ) : null}
                {!solutionId && (
                  <ol
                    className={classnames(
                      styles.faq__resultList,
                      searchResults.didYouMean ? styles.faq__resultListBelowSugestion : null,
                    )}
                    data-test="faq-result-list"
                  >
                    {searchResults.solutions.map(s => {
                      const slug = getSlugForSolution(s);
                      return (
                        <li key={s.id} className={styles.faq__resultListItem}>
                          <Link
                            href={
                              inContactUs
                                ? ""
                                : {
                                    pathname: `/faq/${slug}`,
                                    query: { q: query },
                                  }
                            }
                            shallow={inContactUs}
                            className={styles.faq__resultListLink}
                            data-test="faq-result-list-link"
                            onClick={
                              inContactUs
                                ? e => {
                                    e.preventDefault();
                                    setSolutionId(s.id);
                                  }
                                : null
                            }
                          >
                            {s.title}
                          </Link>
                        </li>
                      );
                    })}
                  </ol>
                )}
                {isValidating && !solution && (
                  <div className={styles.faq__spinner}>
                    <LoadingSpinner />
                  </div>
                )}
                {solution && (
                  <>
                    <div className={styles.faqBack} onClick={() => setSolutionId("")}>
                      <div className={styles.backIcon}>
                        <ChevronLeft />
                      </div>
                      <span>See your results</span>
                    </div>
                    <Solution
                      className={styles.faq__solution}
                      titleClassName={styles["faq__solution-title"]}
                      solution={solution}
                    />
                    {inContactUs && <FAQFeedbackForm onSubmit={handleFeedback} inContactUs solution={solutionId} />}
                    {inContactUs && relatedLoading && (
                      <div className={styles.faq__spinner}>
                        <LoadingSpinner />
                      </div>
                    )}

                    {inContactUs && showRelated && (
                      <FAQRelatedSolutions
                        solutions={related}
                        className={styles["faq__related-solutions"]}
                        inContactUs
                        setSolutionId={setSolutionId}
                        scrollToResult={() =>
                          titleRef.current.scrollIntoView({
                            behavior: "smooth",
                          })
                        }
                        setQuery={setQuery}
                      />
                    )}
                  </>
                )}

                {!inContactUs && <FAQMoreHelp />}
              </>
            ) : (
              noSearchResults
            ))}

          {!resultsLoading && searchError && (
            <p className={styles.faq__error}>
              Sorry, we’re having a technical problem. Try again later or go back to look at the topics on our{" "}
              <Link href="/">Customer Services page</Link>.
            </p>
          )}

          {!inContactUs && loadingPopular && (
            <div className={styles.faq__spinner}>
              <LoadingSpinner />
            </div>
          )}
          {!inContactUs && displayPopular && <FAQPopularSolutions solutions={popular} />}
        </div>
      </div>
    </>
  );
}

FAQ.propTypes = {
  inContactUs: PropTypes.bool,
};

FAQ.defaultProps = {
  inContactUs: false,
};

FAQ.metadata = metadata;

export default FAQ;
